import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Center,
    Flex,
    FormControl,
    FormErrorMessage,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Text,
    VStack
} from '@chakra-ui/react';
import { faEnvelope, faLock } from '@fortawesome/pro-duotone-svg-icons';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API, Auth } from 'aws-amplify';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { FC, useState } from 'react';
import Link from 'src/components/common/link/Link';
import { logSignUp } from 'src/services/firebase';
import OneSignal from 'src/services/onesignal';
import { captureEvent } from 'src/services/posthog';
import { readAttribution } from 'src/utils/helpers/attribution';
import { fetchTrialConfig } from 'src/utils/helpers/trial-config';
import { useAuth } from 'src/utils/hooks/useAuth';
import { useLanguage } from 'src/utils/hooks/useLanguage';
import { object, string } from 'yup';

const RegisterForm: FC = () => {
    const { t } = useTranslation('register');
    const { language } = useLanguage();
    const { handleAuth } = useAuth();
    const router = useRouter();
    const [errorMessage, setErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validationSchema = object().shape({
        email: string().required(t('email-required')).email(t('email-invalid')),
        password: string().required(t('password-required')).min(8, t('password-min-length')),
        confirmPassword: string().test(
            'passwords-match',
            t('passwords-must-match'),
            function (value) {
                return this.parent.password === value;
            }
        )
    });

    type FormValues = {
        email: string;
        password: string;
        confirmPassword: string;
    };

    const initialValues: FormValues = { email: '', password: '', confirmPassword: '' };

    const onSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
        const { email, password } = values;
        try {
            await Auth.signUp({
                username: email.toLowerCase(),
                password
            });
            const user = await Auth.signIn({
                username: email.toLowerCase(),
                password
            });
            handleAuth(user);
            captureEvent({
                event: 'user.signed_up',
                properties: { language, ...readAttribution(), $set: { language } }
            });
            void logSignUp();
            const { newCommunicationFlowEnabled } = await fetchTrialConfig();
            await API.post('rest', '/post-signup-email', {
                body: {
                    email: email.toLowerCase(),
                    language: language,
                    newFlowEnabled: newCommunicationFlowEnabled
                }
            });
            await OneSignal.enable(user.username);
            await OneSignal.setLanguage(language);
            return router.push(`/${language}/user/create`);
        } catch (error: any) {
            let message = t('generic-error-message');
            if (error.code === 'UsernameExistsException') {
                message = t('email-already-exists');
            }
            setErrorMessage(message);
        }
        actions.setSubmitting(false);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}>
            {(props) => {
                const { isSubmitting } = props;

                return (
                    <Form role="form">
                        <Flex
                            flexDirection="column"
                            height="calc(100vh - 144px)"
                            justifyContent="space-between"
                            paddingBottom={16}>
                            <VStack align="stretch" spacing={6}>
                                <Field id="email" name="email">
                                    {({ field, form }: FieldProps) => (
                                        <FormControl
                                            isInvalid={Boolean(
                                                form.errors.email && form.touched.email
                                            )}>
                                            <InputGroup>
                                                <InputLeftElement
                                                    color="lime.300"
                                                    height="100%"
                                                    pointerEvents="none"
                                                    width={16}>
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                </InputLeftElement>
                                                <Input
                                                    id="email"
                                                    paddingLeft={14}
                                                    placeholder={t('email-placeholder')}
                                                    type="email"
                                                    {...field}
                                                />
                                            </InputGroup>
                                            <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field id="password" name="password">
                                    {({ field, form }: FieldProps) => (
                                        <FormControl
                                            isInvalid={Boolean(
                                                form.errors.password && form.touched.password
                                            )}>
                                            <InputGroup>
                                                <InputLeftElement
                                                    alignItems="center"
                                                    color="lime.300"
                                                    display="flex"
                                                    height="100%"
                                                    justifyContent="center"
                                                    pointerEvents="none"
                                                    width={16}>
                                                    <FontAwesomeIcon icon={faLock} />
                                                </InputLeftElement>
                                                <Input
                                                    id="password"
                                                    paddingLeft={14}
                                                    placeholder={t('password-placeholder')}
                                                    type={showPassword ? 'text' : 'password'}
                                                    {...field}
                                                />
                                                <InputRightElement
                                                    alignItems="center"
                                                    cursor="pointer"
                                                    display="flex"
                                                    height="100%"
                                                    justifyContent="center"
                                                    width={16}
                                                    onClick={() => setShowPassword(!showPassword)}>
                                                    <FontAwesomeIcon
                                                        color="gray.800"
                                                        icon={showPassword ? faEyeSlash : faEye}
                                                    />
                                                </InputRightElement>
                                            </InputGroup>
                                            <FormErrorMessage>
                                                {form.errors.password}
                                            </FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field id="confirmPassword" name="confirmPassword">
                                    {({ field, form }: FieldProps) => (
                                        <FormControl
                                            isInvalid={Boolean(
                                                form.errors.confirmPassword &&
                                                    form.touched.confirmPassword
                                            )}>
                                            <InputGroup>
                                                <InputLeftElement
                                                    alignItems="center"
                                                    color="lime.300"
                                                    display="flex"
                                                    height="100%"
                                                    justifyContent="center"
                                                    pointerEvents="none"
                                                    width={16}>
                                                    <FontAwesomeIcon icon={faLock} />
                                                </InputLeftElement>
                                                <Input
                                                    id="confirmPassword"
                                                    paddingLeft={14}
                                                    placeholder={t('confirm-password-placeholder')}
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    {...field}
                                                />
                                                <InputRightElement
                                                    alignItems="center"
                                                    cursor="pointer"
                                                    display="flex"
                                                    height="100%"
                                                    justifyContent="center"
                                                    width={16}
                                                    onClick={() =>
                                                        setShowConfirmPassword(!showConfirmPassword)
                                                    }>
                                                    <FontAwesomeIcon
                                                        color="gray.800"
                                                        icon={
                                                            showConfirmPassword ? faEyeSlash : faEye
                                                        }
                                                    />
                                                </InputRightElement>
                                            </InputGroup>
                                            <FormErrorMessage>
                                                {form.errors.confirmPassword}
                                            </FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                                <Text color="grey.regular" fontSize="sm" textAlign="center">
                                    {t('when-you-register')}{' '}
                                    <Box
                                        as="a"
                                        color="lime.500"
                                        href={`https://www.backresto.com/${language}/terms-and-conditions`}
                                        textDecoration="underline">
                                        {t('terms-and-conditions')}
                                    </Box>{' '}
                                    {t('of-this-service-and-acknowledge')}{' '}
                                    <Box
                                        as="a"
                                        color="lime.500"
                                        href={`https://www.backresto.com/${language}/legal-notice`}
                                        textDecoration="underline">
                                        {t('legal-notice')}
                                    </Box>
                                    .
                                </Text>
                                {errorMessage && (
                                    <Alert bg="purple.50" borderRadius="0.935rem" status="error">
                                        <AlertIcon color="purple.400" />
                                        {errorMessage}
                                    </Alert>
                                )}
                            </VStack>
                            <Box position="relative">
                                <Button
                                    colorScheme="lime"
                                    disabled={isSubmitting}
                                    isLoading={isSubmitting}
                                    type="submit"
                                    width="100%">
                                    {t('continue')}
                                </Button>
                                <Box
                                    bottom={-10}
                                    color="lime.500"
                                    position="absolute"
                                    textDecoration="underline"
                                    width="100%">
                                    <Center>
                                        <Link href="/login">{t('already-have-account')}</Link>
                                    </Center>
                                </Box>
                            </Box>
                        </Flex>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default RegisterForm;
